import { forwardRef, PropsWithChildren, useRef, LegacyRef, useEffect, useState } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { DropDownListHandle } from "@progress/kendo-react-dropdowns";
import { DropdownOption } from "../SearchableDropdown";
import { MultiSelectDropdownOptions } from "./MultiSelectDropdownOption";

interface MultiSelectProviderDropdownProps extends PropsWithChildren {
  name: string;
  className?: string;
  filterable?: boolean;
  providers?: DropdownOption[];
  enableRef: boolean;
  label?: string;
  value: any[];
  onChange?: (e: MultiSelectDropdownOptions[]) => void;
}

export const MultiSelectProviderDropdown = forwardRef(
  (
    props: Readonly<MultiSelectProviderDropdownProps>,
    ref: LegacyRef<DropDownListHandle> | undefined
  ) => {
    const dropdownRef = useRef(null);
    const [options, setOptions] = useState<MultiSelectDropdownOptions[]>([]);
    const [internalValue, setInternalValue] = useState<any[]>([]);

    const transformToMulti = (
      providersArr: DropdownOption[]
    ): MultiSelectDropdownOptions[] => {
      const allProvidersOption = providersArr.find(
        (provider) => provider.value === ""
      );

      const providerOption = providersArr.filter(
        (provider) => provider.value !== ""
      );
      const multiSelectOption = {
        text: allProvidersOption?.text ?? "All Providers",
        value: allProvidersOption?.value ?? "",
        items:
          providerOption?.map((p) => ({
            value: p.value,
            text: p.text,
          })) ?? [],
      };
      return [multiSelectOption];
    };

    // Update options when providers change
    useEffect(() => {
      if (props.providers && props.providers.length > 1) {
        setOptions(transformToMulti(props.providers));
      } else {
        setOptions([]);
      }
    }, [props.providers]);

    useEffect(() => {
      if (!props.value || props.value.length === 0 || !options[0]) {
        setInternalValue([]);
        return;
      }

      const allProvidersSelected = props.value.some(v => v.value === "");

      if (allProvidersSelected) {
        // If "All Providers" selected, select BOTH parent AND all children
        // This ensures the parent shows fully checked not (−)
        const parentOption = options[0];
        if (parentOption && parentOption.items) {
          // Include parent + all child items
          setInternalValue([
            parentOption,
            ...parentOption.items
          ]);
        } else {
          setInternalValue([parentOption]);
        }
      } else {
        // Individual providers selected 
        const parentOption = options[0];
        if (parentOption?.items) {
          const selectedItems = props.value
            .map(v => parentOption.items?.find(item => item.value === v.value))
            .filter(Boolean);
          
          setInternalValue(selectedItems);
        }
      }
    }, [props.value, options]);

    const handleChange = (selections: MultiSelectDropdownOptions[]) => {
      if (!props.onChange) return;

      // Check if parent "All Providers" is in current selections
      const parentSelected = selections.some(s => s.value === "");

      if (parentSelected) {
        props.onChange([{ text: "All Providers", value: "" }]);
      } else if (selections.length === 0) {
        // User deselected everything (including "All Providers")
        // Just pass empty array - clear everything
        props.onChange([]);
      } else {
        // Individual providers selected
        props.onChange(selections);
      }
    };

    return (
      <MultiSelectDropdown
        ref={ref ?? dropdownRef}
        label="Providers"
        name={"Providers"}
        filterable={true}
        options={options}
        onChange={handleChange}
        value={internalValue}
      >
        {props.children}
      </MultiSelectDropdown>
    );
  }
);

export default MultiSelectProviderDropdown;